import { useId } from "react";
import { StatusBadge } from "@/components/primitives";
import { useT } from "@/i18n/I18nProvider";
import type { BoundedGraph } from "./resource-map-bounded";
import type { ResourceMapGraphNode } from "./resource-map-layout";

/**
 * 시각 미리보기의 접근 가능한 대체 (ADR 0025 · Issue #13)
 * --------------------------------------------------------------------------
 * SVG 미리보기는 좌표로 뜻을 전합니다. 좌표를 볼 수 없는 사용자에게 그림은
 * "노드 12개"라는 사실 말고는 아무것도 주지 못합니다. 그래서 **같은 유계
 * 부분집합**을 순서 있는 목록과 한 문장 요약으로 다시 말합니다.
 *
 * - 데이터는 `boundedGraph()` 하나에서 옵니다. 그림과 목록이 다른 것을 말할 수 없습니다.
 * - 노드는 **DOM 순서대로** Tab이 지나갑니다. 그림의 노드 버튼은 절대 배치라
 *   시각 순서와 Tab 순서가 어긋나지만, 이 목록은 서버 순서 그대로입니다.
 * - 선택 동작은 그림과 같은 콜백입니다. 미해석 노드는 양쪽 모두 선택 불가입니다.
 * - 값이 있는 필드(라벨 값·data·env)는 응답에 없고 여기서도 만들지 않습니다.
 */
export interface ResourceMapGraphListProps {
  graph: BoundedGraph;
  totalNodes: number;
  totalEdges: number;
  onSelect: (node: ResourceMapGraphNode) => void;
}

function stateId(node: ResourceMapGraphNode): string {
  if (node.focus) return "resourcemap.alt.node.focus";
  return node.resolved ? "resourcemap.alt.node.resolved" : "resourcemap.alt.node.unresolved";
}

export function ResourceMapGraphList({ graph, totalNodes, totalEdges, onSelect }: ResourceMapGraphListProps) {
  const { t } = useT("resourcemap");
  const titleId = useId();
  const { placed, edges, byKey, degree } = graph;
  const label = (key: string) => {
    const entry = byKey.get(key)?.node;
    if (!entry) return key;
    return `${entry.kind} ${entry.namespace ? `${entry.namespace}/` : ""}${entry.name}`;
  };

  return (
    <section className="resource-map-alt" aria-labelledby={titleId} data-testid="resource-map-graph-list">
      <h4 className="resource-map-alt__title" id={titleId}>
        {t("resourcemap.alt.title")}
      </h4>
      <p className="resource-map-alt__summary" data-testid="resource-map-graph-list-summary">
        {t("resourcemap.alt.summary", { nodes: placed.length, totalNodes, edges: edges.length, totalEdges })}
      </p>
      <p className="resource-map-alt__desc">{t("resourcemap.alt.description")}</p>
      {/* 생략 사실은 그림의 알림(role=status)이 이미 말합니다. 같은 사실을 두 live
          영역이 말하면 스크린리더가 한 번의 변화를 두 번 읽습니다. */}
      {graph.omittedNodes > 0 || graph.omittedEdges > 0 ? (
        <p className="resource-map-alt__omitted">
          {t("resourcemap.alt.omitted", { nodes: graph.omittedNodes, edges: graph.omittedEdges })}
        </p>
      ) : null}

      <ol className="resource-map-alt__list" aria-label={t("resourcemap.alt.nodesLabel")}>
        {placed.map(({ node }) => {
          const selectable = node.resolved && node.uid !== "" && node.resource !== "";
          return (
            <li key={node.key}>
              <button
                type="button"
                className="resource-map-alt__node"
                disabled={!selectable}
                aria-current={node.focus ? "location" : undefined}
                data-testid={`resource-map-alt-node-${node.key}`}
                onClick={() => {
                  if (selectable) onSelect(node);
                }}
              >
                <span className="resource-map-alt__state">{t(stateId(node))}</span>
                <StatusBadge severity={node.health} small />
                {/* Kubernetes kind·namespace·이름은 신원입니다 — 번역하지 않습니다. */}
                <span className="resource-map-alt__name">
                  {node.kind} {node.namespace ? `${node.namespace}/` : ""}
                  {node.name}
                </span>
                <span className="resource-map-alt__degree">
                  {t("resourcemap.alt.node.degree", { count: degree.get(node.key) ?? 0 })}
                </span>
              </button>
            </li>
          );
        })}
      </ol>

      {edges.length === 0 ? (
        <p className="resource-map-alt__empty">{t("resourcemap.alt.emptyEdges")}</p>
      ) : (
        <ol className="resource-map-alt__list" aria-label={t("resourcemap.alt.edgesLabel")}>
          {edges.map((edge, index) => (
            <li key={`${edge.from}|${edge.to}|${edge.type}|${index}`} className="resource-map-alt__edge">
              <span>{t("resourcemap.alt.edge.direction", { from: label(edge.from), to: label(edge.to) })}</span>
              <span className="resource-map-alt__relation">{edge.type}</span>
              {edge.resolved ? null : (
                <span className="resource-map-alt__unresolved">{t("resourcemap.alt.edge.unresolved")}</span>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
